import { CurriculumImportError, curriculumImportIssue, explainCurriculumImportFailure } from "src/utils/curriculumImportIssues";

const LESSON_FIELD_LABELS = {
  learningObjective: "Learning Objectives",
  contentStandards: "Standard(s) for Mathematical Content",
  processStandards: "Standard(s) for Mathematical Process and Practice",
  shortStandard: "Short STD",
  fundamentalKnowledge: "Pre-requisites / Fundamental Knowledge",
  applications: "Applications",
  connections: "Connection to Science/ELA",
  vocabulary: "Key Vocabulary",
  notes: "Notes",
} as const;

export interface CurriculumImportRow {
  sourceRow: number;
  domainName: string;
  unit: string;
  /** @deprecated UI compatibility alias. Use unit. */
  concept?: string;
  lesson: string;
  isLessonHeader: boolean;
  learningObjective: string;
  learningOutcome: string;
  contentStandards: string;
  processStandards: string;
  shortStandard: string;
  fundamentalKnowledge: string;
  applications: string;
  connections: string;
  vocabulary: string;
  notes: string;
  evidenceLabels: string[];
}

const LESSON_FIELD_KEYS = [
  "learningObjective",
  "contentStandards",
  "processStandards",
  "shortStandard",
  "fundamentalKnowledge",
  "applications",
  "connections",
  "vocabulary",
  "notes",
] as const;

export const curriculumWorkbookFingerprint = async (rows: CurriculumImportRow[]): Promise<string> => {
  const content = rows.map((row) => [
    row.sourceRow, row.domainName, row.unit, row.lesson, row.learningObjective, row.learningOutcome,
    row.contentStandards, row.processStandards, row.shortStandard, row.fundamentalKnowledge,
    row.applications, row.connections, row.vocabulary, row.notes, row.evidenceLabels.join("\n"),
  ]).map((row) => row.join("\u001f")).join("\u001e");
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(content));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
};

const clean = (value: unknown) => String(value ?? "").replace(/\r/g, "").trim();

const columnIndex = (header: string[], label: string) => header.findIndex((value) => value.includes(label));

export const buildCurriculumImportRows = (matrix: unknown[][]): CurriculumImportRow[] => {
  const header = (matrix[0] || []).map((value) => clean(value).toLowerCase());
  const domainColumn = columnIndex(header, "domain");
  const unitColumn = header.findIndex((value) => value === "unit" || (value.includes("unit") && !value.includes("lesson")));
  const lessonColumn = columnIndex(header, "lesson name");
  const objectiveColumn = columnIndex(header, "learning objectives");
  const outcomeColumn = columnIndex(header, "learning outcomes");
  const contentColumn = columnIndex(header, "mathematical content");
  const practiceColumn = columnIndex(header, "process and practice");
  const shortColumn = columnIndex(header, "short std");
  const knowledgeColumn = columnIndex(header, "fundamental knowledge");
  const applicationsColumn = columnIndex(header, "applications");
  const connectionsColumn = columnIndex(header, "connection to science");
  const vocabularyColumn = columnIndex(header, "key vocabulary");
  const notesColumn = columnIndex(header, "notes");
  const evidenceColumn = columnIndex(header, "evidence test");
  if ([domainColumn, unitColumn, lessonColumn, outcomeColumn].some((index) => index < 0)) {
    throw new CurriculumImportError(curriculumImportIssue("workbook.missing_columns", { phase: "upload" }));
  }

  const cell = (row: unknown[], index: number) => index < 0 ? "" : clean(row[index]);
  const evidenceLabels = (value: string) => value.split(/\n+/).map((item) => item.trim()).filter(Boolean);
  const parsed = matrix.slice(1).map((row, index) => {
    const unit = cell(row, unitColumn).replace(/^-\s*/, "");
    return {
      sourceRow: index + 2,
      domainName: cell(row, domainColumn),
      unit,
      concept: unit,
      lesson: cell(row, lessonColumn),
      learningObjective: cell(row, objectiveColumn),
      learningOutcome: cell(row, outcomeColumn),
      contentStandards: cell(row, contentColumn),
      processStandards: cell(row, practiceColumn),
      shortStandard: cell(row, shortColumn),
      fundamentalKnowledge: cell(row, knowledgeColumn),
      applications: cell(row, applicationsColumn),
      connections: cell(row, connectionsColumn),
      vocabulary: cell(row, vocabularyColumn),
      notes: cell(row, notesColumn),
      evidenceLabels: evidenceLabels(cell(row, evidenceColumn)),
    };
  }).filter((row) => row.domainName || row.unit || row.lesson || row.learningOutcome);

  const rows: CurriculumImportRow[] = [];
  let groupKey = "";
  for (const row of parsed) {
    if (!row.domainName || !row.unit || !row.lesson) {
      throw new CurriculumImportError(curriculumImportIssue("workbook.row_incomplete", { phase: "upload", sourceRow: row.sourceRow, lesson: row.lesson }));
    }
    const key = `${row.domainName}\u001f${row.unit}\u001f${row.lesson}`;
    const isLessonHeader = key !== groupKey;
    if (!isLessonHeader) {
      const filled = LESSON_FIELD_KEYS.find((field) => row[field].trim());
      if (filled) throw new CurriculumImportError(curriculumImportIssue("workbook.lesson_fields_repeated", { phase: "upload", sourceRow: row.sourceRow, lesson: row.lesson, fieldLabel: LESSON_FIELD_LABELS[filled] }));
    }
    groupKey = key;
    rows.push({ ...row, isLessonHeader });
  }
  if (!rows.length) throw new CurriculumImportError(curriculumImportIssue("workbook.empty", { phase: "upload" }));
  return rows;
};

export const readCurriculumWorkbook = async (file: File, preferredSheetName?: string): Promise<{ sheetName: string; rows: CurriculumImportRow[] }> => {
  const XLSX = await import("xlsx");
  let workbook;
  try {
    workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
  } catch (error) {
    throw new CurriculumImportError(explainCurriculumImportFailure(error, { phase: "upload", code: "workbook.unreadable" }));
  }
  const sheetName = preferredSheetName && workbook.SheetNames.includes(preferredSheetName) ? preferredSheetName : workbook.SheetNames.find((name) => /^grade\s+\d+$/i.test(name)) || workbook.SheetNames[0];
  if (!sheetName) throw new CurriculumImportError(curriculumImportIssue("workbook.no_sheet", { phase: "upload" }));
  const values = XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[sheetName], { header: 1, defval: "", raw: false });
  return { sheetName, rows: buildCurriculumImportRows(values) };
};
